import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, AlertTriangle, Star, Sparkles } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { CASIO_MODELS } from "@/data/casioModels";
import { evaluateExpression, type AngleMode } from "@/lib/calculators/scientific";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Casio Calculator", bn: "ক্যাসিও ক্যালকুলেটর" },
  desc: {
    en: "Free online Casio calculator emulators — FX-991ES Plus, FX-991EX ClassWiz, plus model comparison and a buying guide for Bangladesh students.",
    bn: "ফ্রি অনলাইন ক্যাসিও ক্যালকুলেটর ইমুলেটর — FX-991ES Plus, FX-991EX ক্লাসউইজ, সাথে মডেল তুলনা ও বাংলাদেশী শিক্ষার্থীদের জন্য কেনার গাইড।",
  },
  pickTitle: {
    en: "Which Casio calculator are you looking for?",
    bn: "আপনি কোন ক্যাসিও ক্যালকুলেটরটি খুঁজছেন?",
  },
  open: { en: "Open emulator", bn: "ইমুলেটর খুলুন" },
  popular: { en: "Most popular in BD", bn: "বাংলাদেশে সর্বাধিক জনপ্রিয়" },
  comingSoon: { en: "Coming soon", bn: "শীঘ্রই আসছে" },
  quickCalcTitle: { en: "Quick scientific calculator", bn: "দ্রুত সাইন্টিফিক ক্যালকুলেটর" },
  quickCalcDesc: {
    en: "Just need a Casio-style calculator right now? Use the embedded scientific calculator below.",
    bn: "এখনই একটি ক্যাসিও ধাঁচের ক্যালকুলেটর দরকার? নিচের এম্বেডেড সাইন্টিফিক ক্যালকুলেটরটি ব্যবহার করুন।",
  },
  openFull: { en: "Open full scientific calculator", bn: "সম্পূর্ণ সাইন্টিফিক ক্যালকুলেটর খুলুন" },
  compareTitle: { en: "Casio model comparison", bn: "ক্যাসিও মডেল তুলনা" },
  buyingTitle: { en: "Which Casio should you buy?", bn: "আপনার জন্য কোন ক্যাসিও সঠিক?" },
  whereTitle: { en: "Where to buy in Bangladesh", bn: "বাংলাদেশে কোথায় কিনবেন" },
  fakeTitle: { en: "Spotting counterfeit Casio in Bangladesh", bn: "নকল ক্যাসিও কীভাবে চিনবেন" },
  examTitle: { en: "Casio in exams", bn: "পরীক্ষায় ক্যাসিও" },
  careTitle: { en: "Care and maintenance", bn: "যত্ন ও রক্ষণাবেক্ষণ" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "Best Casio calculator for SSC?", bn: "এসএসসির জন্য সেরা ক্যাসিও কোনটি?" },
    a: {
      en: "FX-100MS is fully sufficient for the SSC syllabus and is affordable. FX-991ES Plus is overkill for SSC alone but a smart purchase if you'll continue to HSC, since it covers everything you'll need through admission tests.",
      bn: "এসএসসি সিলেবাসের জন্য FX-100MS যথেষ্ট ও সাশ্রয়ী। শুধু এসএসসির জন্য FX-991ES Plus অতিরিক্ত, তবে এইচএসসি ও ভর্তি পরীক্ষা পর্যন্ত একই ক্যালকুলেটর চালাতে চাইলে এটি ভালো বিনিয়োগ।",
    },
  },
  {
    q: { en: "FX-991ES vs FX-991EX — which one?", bn: "FX-991ES নাকি FX-991EX — কোনটি কিনব?" },
    a: {
      en: "FX-991EX (ClassWiz) has a higher resolution display, faster processor, spreadsheet mode, distribution functions and QR code output. FX-991ES Plus is older but cheaper, more widely available in Bangladesh, and accepted in every exam. For pure SSC/HSC use, ES Plus is fine; for university engineering, EX is worth the upgrade.",
      bn: "FX-991EX (ClassWiz) এর ডিসপ্লে আরও পরিষ্কার, প্রসেসর দ্রুত, স্প্রেডশিট ও ডিস্ট্রিবিউশন মোড আছে এবং QR কোড দেয়। FX-991ES Plus পুরোনো হলেও সস্তা, বাংলাদেশে সহজলভ্য এবং সব পরীক্ষায় গৃহীত। শুধু এসএসসি/এইচএসসির জন্য ES Plus ই যথেষ্ট; বিশ্ববিদ্যালয় ইঞ্জিনিয়ারিংয়ে EX ভালো।",
    },
  },
  {
    q: { en: "Casio vs Sharp — which brand?", bn: "ক্যাসিও নাকি শার্প — কোন ব্র্যান্ড?" },
    a: {
      en: "Casio dominates the Bangladeshi student market, so teachers, tutorials, and YouTube videos all use Casio button layouts. Sharp calculators are excellent quality but you'll find fewer guides in Bangla. For learning support, Casio wins by ecosystem.",
      bn: "বাংলাদেশের শিক্ষার্থীদের কাছে ক্যাসিও সবচেয়ে জনপ্রিয়, তাই শিক্ষক, টিউটোরিয়াল ও ইউটিউব ভিডিও সবই ক্যাসিও বাটন অনুসারে। শার্পের মান ভালো হলেও বাংলায় গাইড কম পাওয়া যায়। শেখার সুবিধার জন্য ক্যাসিওই এগিয়ে।",
    },
  },
  {
    q: { en: "How to spot a fake Casio?", bn: "নকল ক্যাসিও কীভাবে চিনব?" },
    a: {
      en: "Check the holographic sticker (genuine ones shift color when tilted), the serial number on the back, the printing quality on the buttons (fakes often have blurry secondary labels), and the slide cover finish. Genuine units feel solidly weighted; counterfeits feel lighter and hollow. Buy only from authorized dealers and verify the warranty card.",
      bn: "হলোগ্রাফিক স্টিকার দেখুন (আসল হলে কাত করলে রং বদলায়), পেছনের সিরিয়াল নম্বর যাচাই করুন, বাটনের সেকেন্ডারি লেখার ছাপ পরিষ্কার কি না দেখুন (নকলে অস্পষ্ট), এবং স্লাইড কাভারের ফিনিশিং দেখুন। আসল ইউনিট ভারী মনে হয়, নকল হালকা ও ফাঁপা। শুধু অনুমোদিত ডিলার থেকে কিনুন এবং ওয়ারেন্টি কার্ড যাচাই করুন।",
    },
  },
  {
    q: { en: "ক্যাসিও কোথায় কিনব?", bn: "ক্যাসিও কোথায় কিনব?" },
    a: {
      en: "Buy from Casio Bangladesh authorized dealers — many are listed on Casio's official Bangladesh page. Major book markets (Nilkhet, New Market in Dhaka) also carry calculators, but counterfeits are common there, so verify hologram and serial number before paying. Reputable electronics chains and Daraz authorized stores are safer.",
      bn: "ক্যাসিও বাংলাদেশের অনুমোদিত ডিলার থেকে কিনুন — তালিকা অফিসিয়াল ক্যাসিও বাংলাদেশ পেজে আছে। নীলক্ষেত, নিউমার্কেটেও ক্যালকুলেটর পাওয়া যায়, কিন্তু সেখানে নকল বেশি, তাই কেনার আগে হলোগ্রাম ও সিরিয়াল যাচাই করুন। বড় ইলেকট্রনিক্স চেইন ও দারাজের অনুমোদিত স্টোর তুলনামূলক নিরাপদ।",
    },
  },
  {
    q: { en: "এসএসসির জন্য সেরা ক্যাসিও কোনটি?", bn: "এসএসসির জন্য সেরা ক্যাসিও কোনটি?" },
    a: {
      en: "FX-100MS এসএসসি সিলেবাসের জন্য পুরোপুরি যথেষ্ট। যদি এইচএসসি পর্যন্ত একই ক্যালকুলেটর চালাতে চান, তাহলে FX-991ES Plus কিনুন।",
      bn: "FX-100MS এসএসসি সিলেবাসের জন্য পুরোপুরি যথেষ্ট। যদি এইচএসসি পর্যন্ত একই ক্যালকুলেটর চালাতে চান, তাহলে FX-991ES Plus কিনুন।",
    },
  },
  {
    q: { en: "Are Casio emulators allowed in exams?", bn: "পরীক্ষায় কি ক্যাসিও ইমুলেটর ব্যবহার করা যায়?" },
    a: {
      en: "No — exam halls only allow physical, non-programmable scientific calculators. Use this emulator at home for practice and learning, then bring your physical calculator to the exam.",
      bn: "না — পরীক্ষার হলে শুধুমাত্র শারীরিক, নন-প্রোগ্রামেবল সাইন্টিফিক ক্যালকুলেটর অনুমোদিত। বাড়িতে অনুশীলনের জন্য এই ইমুলেটর ব্যবহার করুন, পরীক্ষায় শারীরিক ক্যালকুলেটর নিয়ে যান।",
    },
  },
  {
    q: { en: "How long does a Casio calculator last?", bn: "একটি ক্যাসিও ক্যালকুলেটর কতদিন চলে?" },
    a: {
      en: "Genuine Casio calculators typically last 5–10 years with normal student use. The FX-991ES Plus and FX-991EX have solar + battery (LR44) backup; the battery only kicks in for low-light conditions, so it can last 2–3 years.",
      bn: "আসল ক্যাসিও ক্যালকুলেটর সাধারণ ছাত্র ব্যবহারে ৫–১০ বছর চলে। FX-991ES Plus ও FX-991EX সোলার + ব্যাটারি (LR44) দুই উৎসে চলে; কম আলোতে শুধু ব্যাটারি কাজ করে, তাই ২–৩ বছর টেকে।",
    },
  },
];

export const Route = createFileRoute("/casio-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Casio Calculator Online — FX-991ES & FX-991EX",
      description:
        "Free online Casio calculator emulators. FX-991ES Plus and FX-991EX ClassWiz for Bangladesh students. Model comparison and buying guide. ক্যাসিও ক্যালকুলেটর।",
      path: "/casio-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Casio Calculator", path: "/casio-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CasioCalculatorHubPage,
});

function CasioCalculatorHubPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const isBn = lang === "bn";

  return (
    <CalculatorLayout
      slug="casio-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calculator"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Section 1: Quick pick */}
      <section>
        <h2 className={cn("mb-4 text-xl font-bold tracking-tight", isBn && "font-bn")}>
          {tr("pickTitle")}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CASIO_MODELS.map((m) => (
            <ModelCard key={m.id} model={m} lang={lang} openLabel={tr("open")} popularLabel={tr("popular")} comingSoonLabel={tr("comingSoon")} />
          ))}
        </div>
      </section>

      {/* Section 2: Embedded calculator */}
      <section className="mt-10">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className={cn("text-xl font-bold tracking-tight", isBn && "font-bn")}>{tr("quickCalcTitle")}</h2>
            <p className={cn("mt-1 text-sm text-muted-foreground", isBn && "font-bn")}>{tr("quickCalcDesc")}</p>
          </div>
          <Link
            to="/scientific-calculator"
            className="hidden shrink-0 items-center gap-1 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent/10 sm:inline-flex"
          >
            {tr("openFull")} <ArrowRight size={12} />
          </Link>
        </div>
        <EmbeddedScientific lang={lang} />
        <div className="mt-3 sm:hidden">
          <Link
            to="/scientific-calculator"
            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            {tr("openFull")} <ArrowRight size={12} />
          </Link>
        </div>
      </section>

      {/* Section 3: Comparison table */}
      <section className="mt-10">
        <h2 className={cn("mb-4 text-xl font-bold tracking-tight", isBn && "font-bn")}>{tr("compareTitle")}</h2>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5">{isBn ? "মডেল" : "Model"}</th>
                <th className="px-3 py-2.5">{isBn ? "ধরন" : "Type"}</th>
                <th className="px-3 py-2.5 text-right">{isBn ? "ফাংশন" : "Functions"}</th>
                <th className="px-3 py-2.5">{isBn ? "ডিসপ্লে" : "Display"}</th>
                <th className="px-3 py-2.5">{isBn ? "দাম (BD)" : "Price (BD)"}</th>
                <th className="px-3 py-2.5">{isBn ? "যাদের জন্য" : "Best for"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {CASIO_MODELS.map((m) => (
                <tr key={m.id} className="hover:bg-accent/5">
                  <td className="px-3 py-2.5 font-semibold">
                    <Link to={m.href as "/casio-fx-991es"} className="hover:text-accent hover:underline">
                      {m.name}
                    </Link>
                  </td>
                  <td className={cn("px-3 py-2.5 text-muted-foreground", isBn && "font-bn")}>{m.type[lang]}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{m.functions}</td>
                  <td className={cn("px-3 py-2.5 text-muted-foreground", isBn && "font-bn")}>{m.display[lang]}</td>
                  <td className="px-3 py-2.5 font-mono">{m.priceBdt}</td>
                  <td className={cn("px-3 py-2.5 text-muted-foreground", isBn && "font-bn")}>{m.bestFor[lang]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 4: Buying guide */}
      <section className="mt-10">
        <h2 className={cn("mb-3 text-xl font-bold tracking-tight", isBn && "font-bn")}>{tr("buyingTitle")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <BuyingTip
            level={isBn ? "এসএসসি শিক্ষার্থী" : "SSC student"}
            tip={
              isBn
                ? "FX-100MS যথেষ্ট। FX-991ES Plus অতিরিক্ত হলেও এইচএসসি পর্যন্ত একই ক্যালকুলেটর চালাতে চাইলে ভালো বিনিয়োগ।"
                : "FX-100MS is sufficient. FX-991ES Plus is overkill for SSC alone, but smart if you'll continue with the same calculator through HSC."
            }
          />
          <BuyingTip
            level={isBn ? "এইচএসসি শিক্ষার্থী" : "HSC student"}
            tip={
              isBn
                ? "FX-991ES Plus মান। বাজেট থাকলে FX-991EX (ClassWiz) আরও দ্রুত ও বেশি ফাংশন দেয়।"
                : "FX-991ES Plus is the standard. If budget allows, FX-991EX (ClassWiz) is faster with more functions."
            }
          />
          <BuyingTip
            level={isBn ? "BUET / ভর্তি পরীক্ষা" : "BUET / admission tests"}
            tip={
              isBn
                ? "FX-991ES Plus বা FX-991EX — দুটোই অনুমোদিত এবং ভর্তি পরীক্ষায় যথেষ্ট।"
                : "FX-991ES Plus or FX-991EX — both are allowed and more than sufficient for admission tests."
            }
          />
          <BuyingTip
            level={isBn ? "বিশ্ববিদ্যালয় ইঞ্জিনিয়ারিং" : "University engineering"}
            tip={
              isBn
                ? "FX-991EX (ClassWiz) সুপারিশ করা হয়। কিছু বিশেষ কোর্সে গ্রাফিং (FX-CG50) লাগে।"
                : "FX-991EX (ClassWiz) recommended. Specialty courses may need a graphing model like FX-CG50."
            }
          />
        </div>
      </section>

      {/* Section 5: Where to buy + counterfeit warning */}
      <section className="mt-10 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-500" />
            <h3 className={cn("text-base font-bold", isBn && "font-bn")}>{tr("whereTitle")}</h3>
          </div>
          <ul className={cn("ml-5 list-disc space-y-1.5 text-sm text-muted-foreground", isBn && "font-bn")}>
            <li>
              {isBn
                ? "ক্যাসিও বাংলাদেশের অনুমোদিত ডিলার থেকে কিনুন — অফিসিয়াল ওয়েবসাইটে তালিকা আছে।"
                : "Buy from Casio Bangladesh authorized dealers — the official site lists them."}
            </li>
            <li>
              {isBn
                ? "বড় ইলেকট্রনিক্স চেইন ও দারাজের অনুমোদিত স্টোর তুলনামূলক নিরাপদ।"
                : "Reputable electronics chains and Daraz authorized stores are relatively safe."}
            </li>
            <li>
              {isBn
                ? "নীলক্ষেত / নিউমার্কেটে কেনার আগে সবসময় হলোগ্রাম ও সিরিয়াল যাচাই করুন।"
                : "If buying from Nilkhet / New Market style book bazaars, always verify the hologram and serial first."}
            </li>
            <li>
              {isBn
                ? "ওয়ারেন্টি কার্ড ও পাকা রসিদ ছাড়া কিনবেন না।"
                : "Never buy without a warranty card and an itemized receipt."}
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h3 className={cn("text-base font-bold text-amber-700 dark:text-amber-400", isBn && "font-bn")}>
              {tr("fakeTitle")}
            </h3>
          </div>
          <ul className={cn("ml-5 list-disc space-y-1.5 text-sm text-muted-foreground", isBn && "font-bn")}>
            <li>
              {isBn
                ? "হলোগ্রাফিক স্টিকার কাত করলে আসলে রং বদলায়; নকলে বদলায় না।"
                : "Genuine holographic stickers shift color when tilted; fakes do not."}
            </li>
            <li>
              {isBn
                ? "পেছনের সিরিয়াল নম্বর পরিষ্কার, একই ফন্টে খোদাই করা থাকে।"
                : "The serial number on the back is sharply etched in a uniform font."}
            </li>
            <li>
              {isBn
                ? "বাটনের উপরে সেকেন্ডারি ফাংশনের লেখা স্পষ্ট ও সরল রেখায় থাকে।"
                : "Secondary function labels above the buttons are crisp and aligned."}
            </li>
            <li>
              {isBn
                ? "আসল ইউনিট ভারী, ভালো গাঁথুনি; নকল হালকা ও ফাঁপা মনে হয়।"
                : "Genuine units feel solidly weighted; counterfeits feel light and hollow."}
            </li>
            <li>
              {isBn
                ? "Casio Bangladesh ওয়েবসাইটে সিরিয়াল যাচাই করার ব্যবস্থা আছে — সন্দেহ হলে যাচাই করুন।"
                : "Casio Bangladesh provides serial verification — when in doubt, verify online."}
            </li>
          </ul>
        </div>
      </section>

      {/* Section 6: Exams + Care */}
      <section className="mt-10 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className={cn("mb-2 text-base font-bold", isBn && "font-bn")}>{tr("examTitle")}</h3>
          <ul className={cn("ml-5 list-disc space-y-1.5 text-sm text-muted-foreground", isBn && "font-bn")}>
            <li>
              {isBn
                ? "এসএসসি / এইচএসসি বোর্ড পরীক্ষায় নন-প্রোগ্রামেবল সাইন্টিফিক ক্যালকুলেটর অনুমোদিত (FX-82MS, FX-100MS, FX-991ES Plus, FX-991EX সহ)।"
                : "SSC / HSC board exams allow non-programmable scientific calculators (including FX-82MS, FX-100MS, FX-991ES Plus, FX-991EX)."}
            </li>
            <li>
              {isBn
                ? "ভর্তি পরীক্ষা (BUET, মেডিকেল, পাবলিক বিশ্ববিদ্যালয়) সাধারণত একই তালিকা অনুসরণ করে — ভর্তি বিজ্ঞপ্তি অবশ্যই দেখে নিন।"
                : "Admission tests (BUET, medical, public universities) usually follow the same list — always check the admission notice."}
            </li>
            <li>
              {isBn
                ? "কিছু পেশাদার পরীক্ষায় (CA, ব্যাংক) আলাদা নিয়ম থাকে — অনেক সময় শুধু সাধারণ ক্যালকুলেটর।"
                : "Some professional exams (CA, bank recruitment) have stricter rules — sometimes only basic calculators are allowed."}
            </li>
            <li>
              {isBn
                ? "FX-50FH সহ প্রোগ্রামেবল মডেল বেশিরভাগ পরীক্ষায় নিষিদ্ধ।"
                : "Programmable models like FX-50FH are banned in most exams."}
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className={cn("mb-2 text-base font-bold", isBn && "font-bn")}>{tr("careTitle")}</h3>
          <ul className={cn("ml-5 list-disc space-y-1.5 text-sm text-muted-foreground", isBn && "font-bn")}>
            <li>
              {isBn
                ? "ব্যবহার শেষে স্লাইড কাভার লাগিয়ে রাখুন — ডিসপ্লেতে চাপ পড়ে ফাটে।"
                : "Always slide the cover on after use — display cracks come from pressure in your bag."}
            </li>
            <li>
              {isBn
                ? "ব্যাটারি (LR44) ২–৩ বছর চলে; ডিসপ্লে ম্লান হলে পরিবর্তন করুন।"
                : "The LR44 battery lasts 2–3 years; replace it when the display dims."}
            </li>
            <li>
              {isBn
                ? "সরাসরি রোদে / ভেজা জায়গায় রাখবেন না; ক্ষারীয় ব্যাটারি লিক হলে যন্ত্র নষ্ট হয়।"
                : "Avoid direct sun and humidity; leaking alkaline cells will destroy the calculator."}
            </li>
            <li>
              {isBn
                ? "বাটন আটকে গেলে শুকনো কাপড় দিয়ে মুছুন — তরল ক্লিনার নয়।"
                : "Clean sticky buttons with a dry cloth — not liquid cleaners."}
            </li>
          </ul>
        </div>
      </section>

      {/* About */}
      <article className={cn("prose prose-sm mt-10 max-w-none dark:prose-invert", isBn && "font-bn")}>
        <h2>{isBn ? "ক্যাসিও সাইন্টিফিক ক্যালকুলেটর সম্পর্কে" : "About Casio scientific calculators"}</h2>
        <p>
          {isBn
            ? "Casio Computer Co., Ltd. ১৯৫৭ সাল থেকে ক্যালকুলেটর তৈরি করছে এবং বিশ্বব্যাপী শিক্ষার্থীদের কাছে সবচেয়ে জনপ্রিয় ব্র্যান্ড। FX সিরিজ বিশেষভাবে স্কুল ও বিশ্ববিদ্যালয়ের জন্য তৈরি, প্রতিটি মডেল নির্দিষ্ট সিলেবাস অনুযায়ী ফাংশন দেয়।"
            : "Casio Computer Co., Ltd. has produced calculators since 1957 and is the most widely-used student calculator brand worldwide. The FX series is purpose-built for school and university curricula, with each model targeting a specific syllabus level."}
        </p>
        <h2>{isBn ? "বাংলাদেশের শিক্ষায় ক্যাসিও" : "Casio in Bangladesh education"}</h2>
        <p>
          {isBn
            ? "বাংলাদেশের এসএসসি, এইচএসসি, ভর্তি পরীক্ষা ও বিশ্ববিদ্যালয়ের বেশিরভাগ শিক্ষার্থী ক্যাসিও ব্যবহার করে। শিক্ষকেরা ক্লাসে ক্যাসিওর বাটন অনুযায়ী নির্দেশনা দেন, এবং ইউটিউবে বাংলায় তৈরি প্রায় সব ক্যালকুলেটর টিউটোরিয়াল ক্যাসিও কেন্দ্রিক। ফলে শেখার ইকোসিস্টেমেও ক্যাসিও এগিয়ে।"
            : "Most Bangladeshi students from SSC through university use Casio. Teachers give in-class instructions based on Casio button layouts, and nearly every Bangla YouTube tutorial uses a Casio. The learning ecosystem itself is Casio-first."}
        </p>
        <h2>{isBn ? "নিজের জন্য সঠিক ক্যাসিও বেছে নেওয়া" : "Choosing the right Casio for your level"}</h2>
        <p>
          {isBn
            ? "আপনার বর্তমান শ্রেণি ও পরবর্তী ২–৩ বছরের পরিকল্পনা অনুযায়ী মডেল বেছে নিন। সামনে এইচএসসি বা ভর্তি পরীক্ষা থাকলে FX-991ES Plus সবচেয়ে নিরাপদ পছন্দ — একবার কিনলে অনেক বছর চলে যায়।"
            : "Pick a model that fits your current level and the next 2–3 years of study. If HSC or admission tests are ahead, FX-991ES Plus is the safest pick — buy once and use it for years."}
        </p>
      </article>

      {/* Related */}
      <section className="mt-8">
        <h2 className={cn("mb-3 text-base font-bold", isBn && "font-bn")}>
          {isBn ? "সম্পর্কিত ক্যালকুলেটর" : "Related calculators"}
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <RelatedChip to="/casio-fx-991es" label={isBn ? "Casio FX-991ES" : "Casio FX-991ES"} />
          <RelatedChip to="/casio-fx-991ex" label={isBn ? "Casio FX-991EX" : "Casio FX-991EX"} />
          <RelatedChip to="/scientific-calculator" label={isBn ? "সাইন্টিফিক ক্যালকুলেটর" : "Scientific Calculator"} />
          <RelatedChip to="/math-solver" label={isBn ? "ম্যাথ সলভার" : "Math Solver"} />
          <RelatedChip to="/graphing-calculator" label={isBn ? "গ্রাফিং ক্যালকুলেটর" : "Graphing Calculator"} />
        </div>
      </section>
    </CalculatorLayout>
  );
}

/* ------- Components ------- */

function ModelCard({
  model,
  lang,
  openLabel,
  popularLabel,
  comingSoonLabel,
}: {
  model: typeof CASIO_MODELS[number];
  lang: "en" | "bn";
  openLabel: string;
  popularLabel: string;
  comingSoonLabel: string;
}) {
  const isBn = lang === "bn";
  return (
    <Link
      to={model.href as "/casio-fx-991es"}
      className={cn(
        "group relative flex flex-col rounded-xl border bg-card p-4 transition hover:border-accent hover:shadow-md",
        model.popular ? "border-accent/60" : "border-border",
      )}
    >
      {model.popular && (
        <span className="absolute -top-2 left-3 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-foreground">
          <Star size={10} /> {popularLabel}
        </span>
      )}
      {model.comingSoon && (
        <span className="absolute -top-2 right-3 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          <Sparkles size={10} /> {comingSoonLabel}
        </span>
      )}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-base font-bold">{model.name}</div>
          <div className={cn("mt-0.5 text-xs text-muted-foreground", isBn && "font-bn")}>{model.type[lang]}</div>
        </div>
      </div>
      <div className={cn("mt-2 text-xs text-muted-foreground", isBn && "font-bn")}>
        {model.bestFor[lang]}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="font-mono text-muted-foreground">{model.priceBdt}</span>
        <span className="inline-flex items-center gap-1 font-semibold text-accent group-hover:underline">
          {model.isEmulator ? openLabel : (model.noteForLink?.[lang] ?? openLabel)}
          <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function BuyingTip({ level, tip }: { level: string; tip: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <div className="text-xs font-bold uppercase tracking-wide text-accent">{level}</div>
      <p className="mt-1 text-sm text-muted-foreground">{tip}</p>
    </div>
  );
}

function RelatedChip({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to as "/scientific-calculator"}
      className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10"
    >
      {label}
    </Link>
  );
}

/* ------- Embedded scientific calculator ------- */

type Btn =
  | { label: string; insert: string; kind?: "num" | "op" | "fn" | "ctrl" | "eq" | "del" }
  | { label: string; action: "ac" | "del" | "eq" | "shift"; kind?: "ctrl" | "del" | "eq" }
  | { label: string; shiftLabel?: string; insert: string; shiftInsert?: string; kind?: "fn" | "op" | "num" };

function EmbeddedScientific({ lang }: { lang: "en" | "bn" }) {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("0");
  const [err, setErr] = useState<string | null>(null);
  const [angle, setAngle] = useState<AngleMode>("DEG");
  const [shift, setShift] = useState(false);
  const [ans, setAns] = useState<number | null>(null);

  const insert = useCallback((s: string) => {
    setErr(null);
    setExpr((p) => p + s);
    setShift(false);
  }, []);
  const del = useCallback(() => {
    setErr(null);
    setExpr((p) => p.slice(0, -1));
  }, []);
  const ac = useCallback(() => {
    setExpr("");
    setResult("0");
    setErr(null);
  }, []);
  const evalNow = useCallback(() => {
    if (!expr.trim()) return;
    const r = evaluateExpression(expr, { angleMode: angle, displayMode: "NORM", fixDigits: 6, ans, memory: 0 });
    if (r.ok) {
      setResult(r.display);
      setErr(null);
      setAns(r.value);
    } else {
      setErr(r.message);
    }
  }, [expr, angle, ans]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const k = e.key;
      if (/^[0-9.]$/.test(k)) return insert(k);
      if (k === "+" || k === "-") return insert(k);
      if (k === "*") return insert("×");
      if (k === "/") return insert("÷");
      if (k === "(" || k === ")" || k === "%" || k === "^") return insert(k);
      if (k === "p" || k === "P") return insert("π");
      if (k === "Enter" || k === "=") { e.preventDefault(); return evalNow(); }
      if (k === "Backspace") { e.preventDefault(); return del(); }
      if (k === "Escape") return ac();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [insert, evalNow, del, ac]);

  type GridBtn = {
    label: string;
    shiftLabel?: string;
    onPress: () => void;
    kind?: "num" | "op" | "fn" | "ctrl" | "eq" | "del";
  };

  const ins = (s: string) => () => insert(s);
  const grid: GridBtn[][] = [
    [
      { label: "SHIFT", onPress: () => setShift((s) => !s), kind: "ctrl" },
      { label: angle, onPress: () => setAngle((m) => (m === "DEG" ? "RAD" : "DEG")), kind: "ctrl" },
      { label: "AC", onPress: ac, kind: "del" },
      { label: "DEL", onPress: del, kind: "del" },
    ],
    [
      { label: "sin", shiftLabel: "sin⁻¹", onPress: shift ? ins("asin(") : ins("sin("), kind: "fn" },
      { label: "cos", shiftLabel: "cos⁻¹", onPress: shift ? ins("acos(") : ins("cos("), kind: "fn" },
      { label: "tan", shiftLabel: "tan⁻¹", onPress: shift ? ins("atan(") : ins("tan("), kind: "fn" },
      { label: "log", shiftLabel: "10ˣ", onPress: shift ? ins("10^") : ins("log("), kind: "fn" },
    ],
    [
      { label: "ln", shiftLabel: "eˣ", onPress: shift ? ins("e^") : ins("ln("), kind: "fn" },
      { label: "√", onPress: ins("sqrt("), kind: "fn" },
      { label: "x²", onPress: ins("^2"), kind: "fn" },
      { label: "xʸ", onPress: ins("^"), kind: "fn" },
    ],
    [
      { label: "(", onPress: ins("("), kind: "op" },
      { label: ")", onPress: ins(")"), kind: "op" },
      { label: "π", onPress: ins("π"), kind: "fn" },
      { label: "÷", onPress: ins("÷"), kind: "op" },
    ],
    [
      { label: "7", onPress: ins("7"), kind: "num" },
      { label: "8", onPress: ins("8"), kind: "num" },
      { label: "9", onPress: ins("9"), kind: "num" },
      { label: "×", onPress: ins("×"), kind: "op" },
    ],
    [
      { label: "4", onPress: ins("4"), kind: "num" },
      { label: "5", onPress: ins("5"), kind: "num" },
      { label: "6", onPress: ins("6"), kind: "num" },
      { label: "−", onPress: ins("-"), kind: "op" },
    ],
    [
      { label: "1", onPress: ins("1"), kind: "num" },
      { label: "2", onPress: ins("2"), kind: "num" },
      { label: "3", onPress: ins("3"), kind: "num" },
      { label: "+", onPress: ins("+"), kind: "op" },
    ],
    [
      { label: "0", onPress: ins("0"), kind: "num" },
      { label: ".", onPress: ins("."), kind: "num" },
      { label: "Ans", onPress: ins("Ans"), kind: "fn" },
      { label: "=", onPress: evalNow, kind: "eq" },
    ],
  ];

  return (
    <div className="rounded-xl border border-border bg-zinc-100 p-3 shadow-inner dark:bg-zinc-900">
      {/* Display */}
      <div className="mb-3 rounded-lg border border-border bg-emerald-50 px-3 py-2 font-mono text-right shadow-inner dark:bg-emerald-950/40">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>{angle}{shift ? " • SHIFT" : ""}</span>
          <span>{lang === "bn" ? "ক্যাসিও মোড" : "Casio mode"}</span>
        </div>
        <div className="min-h-[20px] truncate text-sm text-muted-foreground">{expr || (lang === "bn" ? "এক্সপ্রেশন…" : "expression…")}</div>
        <div className={cn("text-2xl font-bold", err ? "text-red-500" : "text-foreground")}>
          {err ? (lang === "bn" ? "ত্রুটি" : "Error") : result}
        </div>
        {err && <div className="text-[10px] text-red-500">{err}</div>}
      </div>

      {/* Keypad */}
      <div className="grid gap-1.5">
        {grid.map((row, ri) => (
          <div key={ri} className="grid grid-cols-4 gap-1.5">
            {row.map((b, bi) => (
              <Button
                key={bi}
                onClick={b.onPress}
                variant="outline"
                className={cn(
                  "relative h-11 px-0 text-sm font-semibold",
                  b.kind === "num" && "bg-card",
                  b.kind === "op" && "bg-amber-500/10 text-amber-700 dark:text-amber-400",
                  b.kind === "fn" && "bg-blue-500/10 text-blue-700 dark:text-blue-400 text-xs",
                  b.kind === "ctrl" && "bg-yellow-400/20 text-yellow-700 dark:text-yellow-400 text-xs",
                  b.kind === "del" && "bg-red-500/10 text-red-700 dark:text-red-400 text-xs",
                  b.kind === "eq" && "bg-accent text-accent-foreground hover:bg-accent/90",
                )}
              >
                {b.shiftLabel && shift && (
                  <span className="absolute -top-1 right-1 text-[8px] font-bold text-yellow-600 dark:text-yellow-400">
                    {b.shiftLabel}
                  </span>
                )}
                {b.label}
              </Button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
